"use client";
import { getSelectedLanguage, loadLanguages } from "@/utils/serverUtils";
import { Icon } from "@iconify/react";
import Cookies from "js-cookie";
import { usePathname, useRouter } from "next/navigation";
import { useEffect, useRef, useState } from "react";

const LanguageDropdown = ({ authpage = false }) => {
  const [languageDropdown, setLanguageDropdown] = useState(false);
  const [languages, setLanguages] = useState([]);
  const [selectedLang, setSelectedLang] = useState(null);

  const dropdownRef = useRef(null);
  const pathname = usePathname();
  const router = useRouter();

  useEffect(() => {
    const getLanguages = async () => {
      try {
        const languages = await loadLanguages();
        setLanguages(languages);

        // Get current locale
        const locale = Cookies.get("NEXT_LOCALE") || "en";
        const selectedLanguage = getSelectedLanguage(languages, locale);

        setSelectedLang(selectedLanguage);
        applyLanguageSettings(selectedLanguage);
      } catch (err) {
        console.error("Language load error:", err);
      }
    };

    getLanguages();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setLanguageDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    setLanguageDropdown(false);
  }, [pathname]);

  const applyLanguageSettings = (lang) => {
    document.documentElement.dir = lang.is_rtl ? "rtl" : "ltr";
    document.documentElement.lang = lang.locale;
    document.documentElement.setAttribute("data-language", lang.locale);
  };

  const handleSelect = (lang) => {
    setSelectedLang(lang);
    setLanguageDropdown(false);

    // Apply visual settings immediately
    applyLanguageSettings(lang);

    // Update cookie
    Cookies.set("NEXT_LOCALE", lang.locale, { expires: 365 });

    // Dispatch event for TranslationProvider
    window.dispatchEvent(
      new CustomEvent("languageChange", {
        detail: { language: lang },
      })
    );

    // Refresh to reload server components
    router.refresh();
  };

  if (!selectedLang) return null;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        className={`h-[40px] sm:h-[40px] flex items-center gap-0 md:gap-[10px] cursor-pointer px-[10px] md:px-[16px] rounded-[12px] ${
          authpage ? "bg-[#dfd6fa]" : "bg-white"
        }`}
        onClick={() => setLanguageDropdown((prev) => !prev)}
      >
        <Icon
          icon="lucide:globe"
          className="text-user-text"
          width="18"
          height="18"
        />
        <span className="text-user-text text-[14px] font-semibold">
          <span className="hidden md:inline">{selectedLang.name}</span>
        </span>
      </button>

      <div
        className={`absolute ltr:right-0 rtl:left-0 mt-[10px] w-[153px] bg-white border-2 border-[rgba(116,69,255,0.16)]
        rounded-[16px] shadow-lg z-10 transform transition-all duration-300 origin-top
        ${
          languageDropdown
            ? "opacity-100 scale-y-100"
            : "opacity-0 scale-y-0 pointer-events-none"
        }`}
      >
        <ul>
          {languages.map((lang) => (
            <li key={lang.locale}>
              <button
                onClick={() => handleSelect(lang)}
                className={`w-full px-[16px] py-[10px] ltr:text-left rtl:text-right text-[14px] font-semibold transition-colors
                  ${
                    selectedLang.locale === lang.locale
                      ? "text-user-primary"
                      : "text-user-text hover:text-user-primary"
                  }`}
              >
                {lang.name}
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default LanguageDropdown;
